import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  Database, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { getCacheStats, getOptimizationStats } from '../../utils/cacheManager';

const PerformanceMonitor = () => {
  const [cacheStats, setCacheStats] = useState({});
  const [optimizationStats, setOptimizationStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const updateStats = () => {
      try {
        // Updating performance stats
        const cacheStatsData = getCacheStats();
        const optimizationStatsData = getOptimizationStats();
        
        setCacheStats(cacheStatsData);
        setOptimizationStats(optimizationStatsData);
        setError(null);
      } catch (error) {
        // Error updating stats
        setError(error.message);
        // Set default values if functions fail
        setCacheStats({});
        setOptimizationStats({});
      }
      setLoading(false);
    };

    // Initial load
    updateStats();

    // Update every 5 seconds
    const interval = setInterval(updateStats, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (value, threshold, reverse = false) => {
    const isGood = reverse ? value <= threshold : value >= threshold;
    return isGood ? 'text-green-600' : 'text-red-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading performance data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Error Loading Performance Data</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Activity className="w-8 h-8 mr-3 text-blue-500" />
                Performance Monitor
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Real-time monitoring of cache performance, Firebase operations, and Web Vitals
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Cache Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Database className="w-5 h-5 mr-2 text-blue-500" />
              Cache Performance
            </h2>
            
            <div className="space-y-4">
              {Object.entries(cacheStats).length > 0 ? (
                Object.entries(cacheStats).map(([cacheName, stats]) => (
                  <div key={cacheName} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 dark:text-white mb-2 capitalize">
                      {cacheName} Cache
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">Hit Rate</p>
                        <p className={`font-semibold ${getStatusColor(parseFloat(stats.hitRate || '0'), 80)}`}>
                          {stats.hitRate || '0%'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">Size</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {stats.size || 0} / {stats.maxSize || 'N/A'}
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">Memory</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {stats.memoryUsageMB || '0.00'} MB
                        </p>
                      </div>
                      
                      <div>
                        <p className="text-gray-600 dark:text-gray-400">Evictions</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {stats.evictions || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No cache data available</p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Cache system may not be initialized yet</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Firebase Operations */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-green-500" />
              Firebase Operations
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Reads</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {optimizationStats.reads || 0}
                  </p>
                </div>
                
                <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Writes</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {optimizationStats.writes || 0}
                  </p>
                </div>
                
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Batches</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {optimizationStats.batches || 0}
                  </p>
                </div>
                
                <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Cache Hit Rate</p>
                  <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                    {((optimizationStats.cacheHitRate || 0) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
              
              {Object.keys(optimizationStats).length === 0 && (
                <div className="text-center py-4">
                  <TrendingUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No Firebase optimization data available</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>

        {/* Performance Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-yellow-500" />
            Performance Recommendations
          </h2>
          
          <div className="space-y-3">
            {Object.entries(cacheStats).map(([cacheName, stats]) => {
              const recommendations = [];
              const hitRate = parseFloat(stats.hitRate || '0');
              
              if (hitRate < 80) {
                recommendations.push(
                  `${cacheName} cache hit rate is low (${stats.hitRate || '0%'}). Consider increasing cache size or optimizing data access patterns.`
                );
              }
              
              const memoryUsage = parseFloat(stats.memoryUsageMB || '0');
              if (memoryUsage > 40) { // Assuming 50MB max, warn at 80%
                recommendations.push(
                  `${cacheName} cache memory usage is high (${stats.memoryUsageMB || '0.00'} MB). Consider reducing cache size or implementing more aggressive eviction.`
                );
              }
              
              return recommendations.map((rec, index) => (
                <div key={`${cacheName}-${index}`} className="flex items-start p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">{rec}</p>
                </div>
              ));
            })}
            
            {Object.keys(cacheStats).length === 0 && (
              <div className="flex items-start p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-blue-800 dark:text-blue-200">No cache data available. Cache system may not be initialized yet.</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PerformanceMonitor;
